import React, { useState } from "react";
import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import useDebounce from "@shared/hooks/useDebounce";
import { formatDate } from "@shared/utils/dates/formatting";
import HighlightMatchingText from "@ui-kit/data-display/HighlightMatchingText/HighlightMatchingText.web";
import SearchItem from "@ui-kit/data-display/SearchItem/SearchItem.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import StatusBadge from "@shared/components/application/data-display/badges/StatusBadge/StatusBadge.web";
import Icon from "@ui-kit/icons/Icon.web";
import Switch from "@ui-kit/form/Switch/Switch.web";
import {
  SelectedItemCard,
  SearchFieldCounter,
  SelectedItemTags,
  SelectedItemList,
} from "@shared/components/application/form/FormField/SearchFieldShared.web";
import type { SubItemType } from "@shared/components/application/form/FormField/types";
import { useTranslation } from "@shared/utils/internationalization";
import useSearchSharedState from "../search-shared/useSearchSharedState";
import useSearchEvent from "./hooks/useSearchEvent";
import type {
  EventSearchItemType,
  SearchEventSharedProps,
} from "./SearchEvent.props";

const DEFAULT_SUB_ITEMS_KEY = "tickettype_set";

export type SearchEventProps = SearchEventSharedProps & {
  // Web-specific props
};

type SearchEventComponent = React.FC<SearchEventProps> & {
  BaseEventListTemplate: (args: {
    item: VenueBasedEventSerializer;
    searchText?: string;
  }) => JSX.Element;
  BaseMatchTemplate: (args: { item: VenueBasedEventSerializer }) => JSX.Element;
};

const SearchEvent: SearchEventComponent = ({
  single,
  listItemTemplate,
  value,
  service,
  searchParams,
  fetchOptions,
  onChange,
  onSelect,
  onRemove,
  showSelectedBelow = false,
  withSubItems = false,
  renderSelectedItem,
  counters,
  getFormValue,
  filterOptions,
  subItemsKey = DEFAULT_SUB_ITEMS_KEY,
  selectedItemsDisplay = "cards",
  renderSelected,
  showPastEventsToggle = false,
  ...rest
}: SearchEventProps): JSX.Element => {
  const { t } = useTranslation("dashboard");
  const [inputValue, setInputValue] = useState("");
  const [debouncedInput] = useDebounce(inputValue, 500);
  const [showPastEvents, setShowPastEvents] = useState(false);

  const useFetchOpts = Boolean(fetchOptions && !service);

  const serviceResult = useSearchEvent({
    eventSearchInput: debouncedInput,
    single,
    customParams: (searchParams ?? {}) as Record<string, unknown>,
    service,
    enabled: !useFetchOpts,
    showPastEvents: showPastEventsToggle ? showPastEvents : undefined,
  });

  const {
    selectedSet,
    filteredItems,
    counterItems,
    subItemSelections,
    displayMode,
    handleAdd,
    handleRemove,
    handleSingleSelect,
    handleToggleSubItem,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    singleValue: resolvedSingleValue,
  } = useSearchSharedState<EventSearchItemType>({
    serviceResult,
    fetchOptions,
    service,
    debouncedQuery: debouncedInput,
    single,
    value,
    onChange,
    onSelect,
    onRemove,
    filterOptions,
    withSubItems,
    subItemsKey,
    counters,
    getFormValue,
    selectedItemsDisplay,
    showSelectedBelow,
  });

  const EventListItemTemplate = (item: EventSearchItemType) => {
    if (listItemTemplate) {
      return listItemTemplate(item, String(inputValue));
    }
    return (
      <SearchEvent.BaseEventListTemplate item={item} searchText={inputValue} />
    );
  };

  const renderEventCard = (
    item: EventSearchItemType,
    helpers: {
      subItems: SubItemType[] | undefined;
      selectedSubIds: number[];
      onDelete: () => void;
      onToggleSubItem?: (subItemId: number, isSelected: boolean) => void;
    }
  ): React.ReactNode => {
    let badge: React.ReactNode = null;
    if (String(item.status) === "template") {
      badge = <StatusBadge status="info">Template</StatusBadge>;
    } else if (item.is_recurring_parent) {
      badge = <StatusBadge status="info">Parent</StatusBadge>;
    }
    return (
      <SelectedItemCard
        key={item.id}
        title={item.name ?? ""}
        subtitle={
          item.starts_on
            ? formatDate({
                date: item.starts_on,
                displayTime: true,
                timezone: item.timezone ?? null,
                displayTimezone: true,
              })
            : undefined
        }
        badge={badge}
        subItems={withSubItems ? helpers.subItems : undefined}
        selectedSubItemIds={helpers.selectedSubIds}
        onDelete={helpers.onDelete}
        onToggleSubItem={withSubItems ? helpers.onToggleSubItem : undefined}
      />
    );
  };

  return (
    <Flex direction="column" gap="md" width="100%">
      {showSelectedBelow &&
        counterItems.length > 0 &&
        selectedSet.length > 0 && <SearchFieldCounter items={counterItems} />}

      <SearchItem<EventSearchItemType>
        {...rest}
        InputLeftElementIcon={<Icon.MagnifyingGlass.Outline color="fg.muted" />}
        single={single}
        multiple={!single}
        items={filteredItems}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        listItemTemplate={EventListItemTemplate}
        selectedSet={showSelectedBelow ? [] : selectedSet}
        value={single ? resolvedSingleValue : undefined}
        onDropdownClickChange={single ? undefined : handleAdd}
        handleSelectedItemChange={
          single
            ? handleSingleSelect
            : (item) => {
                if (item) handleRemove(item.id);
              }
        }
        onInputValueChange={(change) => setInputValue(change)}
        handleInputValueChange={(change) => {
          setInputValue(change);
        }}
        renderSelected={renderSelected}
      />

      {showPastEventsToggle && (
        <Flex justifyContent="flex-end">
          <Switch
            data-testid="search-past-events-toggle"
            label={t("Search past events")}
            checked={showPastEvents}
            onChange={(e) => setShowPastEvents(e.target.checked)}
          />
        </Flex>
      )}

      {showSelectedBelow && selectedSet.length > 0 && (
        <>
          {renderSelectedItem &&
            selectedSet.map((item) => {
              const subItems = item[
                subItemsKey as keyof EventSearchItemType
              ] as SubItemType[] | undefined;
              return (
                <React.Fragment key={item.id}>
                  {renderSelectedItem({
                    item,
                    onRemove: () => handleRemove(item.id),
                    subItems,
                    selectedSubItemIds: subItemSelections.get(item.id) ?? [],
                    onToggleSubItem: withSubItems
                      ? (subId, selected) =>
                          handleToggleSubItem(item.id, subId, selected)
                      : undefined,
                    isLoading: false,
                  })}
                </React.Fragment>
              );
            })}

          {!renderSelectedItem && displayMode === "tags" && (
            <SelectedItemTags
              items={selectedSet}
              onRemove={(id) => handleRemove(id)}
            />
          )}

          {!renderSelectedItem && displayMode === "list" && (
            <SelectedItemList
              items={selectedSet}
              onRemove={(id) => handleRemove(id)}
            />
          )}

          {!renderSelectedItem && displayMode === "cards" && (
            <Flex direction="column" gap="md">
              {selectedSet.map((item) => {
                const subItems = item[
                  subItemsKey as keyof EventSearchItemType
                ] as SubItemType[] | undefined;
                const selectedSubIds = subItemSelections.get(item.id) ?? [];
                return (
                  <React.Fragment key={item.id}>
                    {renderEventCard(item, {
                      subItems,
                      selectedSubIds,
                      onDelete: () => handleRemove(item.id),
                      onToggleSubItem: withSubItems
                        ? (subId, selected) =>
                            handleToggleSubItem(item.id, subId, selected)
                        : undefined,
                    })}
                  </React.Fragment>
                );
              })}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};

SearchEvent.displayName = "SearchEvent";

SearchEvent.BaseEventListTemplate = function BaseEventListTemplate({
  item,
  searchText = "",
}: {
  item: VenueBasedEventSerializer;
  searchText?: string;
}): JSX.Element {
  const { name, id, starts_on, ends_on, timezone } = item;

  return (
    <>
      <Box
        padding="0 7px 5px 7px"
        data-testid={`search-combobox-option-event-${id}`}
      >
        <Text
          isTruncated
          fontSize="md"
          data-testid={`search-combobox-option-event-${id}-name`}
        >
          <HighlightMatchingText
            text={String(name)}
            matchingText={searchText}
          />
        </Text>
        <Text
          isTruncated
          fontSize="xs"
          data-testid={`search-combobox-option-event-${id}-time`}
        >
          {starts_on &&
            formatDate({
              date: starts_on,
              displayTime: true,
              displayTimezone: true,
              timezone: timezone ?? null,
            })}
          {ends_on && (
            <>
              {" - "}
              {formatDate({
                date: ends_on,
                displayTime: true,
                displayTimezone: true,
                timezone: timezone ?? null,
              })}
            </>
          )}
        </Text>
      </Box>
    </>
  );
};

SearchEvent.BaseMatchTemplate = function BaseMatchTemplate({
  item,
}: {
  item: VenueBasedEventSerializer;
}): JSX.Element {
  const { name: displayName, starts_on: date } = item;

  return (
    <Flex direction="column" alignItems="flex-start" maxWidth="container.xs">
      <Text isTruncated mr="xs" fontSize="md" width="100%">
        {displayName}
      </Text>

      <Text
        isTruncated
        mr="xs"
        fontSize="xs"
        width="100%"
        data-testid={`search-combobox-option-event-${item.id}-time`}
      >
        {date}
      </Text>
    </Flex>
  );
};

export default SearchEvent;
